// ==================== CUSTOMER DASHBOARD CONSTANTS ====================

import {
  Users,
  MessageSquare,
  Send,
  FileText,
  Bot,
  CheckCircle2,
  Clock,
  AlertCircle,
  XCircle,
  Zap,
  TrendingUp,
  TrendingDown,
  type LucideIcon,
} from 'lucide-react';

// ==================== DATE RANGE OPTIONS ====================

export const DATE_RANGE_OPTIONS = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
] as const;

// ==================== STAT CARD CONFIG ====================

export interface StatCardConfig {
  key: string;
  title: string;
  icon: LucideIcon;
  color: string;
  bgColor: string;
  valueFormatter?: (value: number) => string;
}

export const STATS_CARDS_CONFIG: StatCardConfig[] = [
  {
    key: 'contacts',
    title: 'Total Contacts',
    icon: Users,
    color: 'text-(--color-signal-orange)',
    bgColor: 'bg-(--color-orange-glow)',
  },
  {
    key: 'messages',
    title: 'Messages Sent',
    icon: MessageSquare,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
  },
  {
    key: 'campaigns',
    title: 'Active Campaigns',
    icon: Send,
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
  },
  {
    key: 'templates',
    title: 'Templates',
    icon: FileText,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
  },
];

// ==================== QUICK ACTIONS CONFIG ====================

export interface QuickAction {
  id: string;
  label: string;
  icon: LucideIcon;
  href: string;
  color?: string;
}

export const QUICK_ACTIONS: QuickAction[] = [
  { id: 'new-campaign', label: 'New Campaign', icon: Send, href: '/app/campaigns' },
  { id: 'new-template', label: 'New Template', icon: FileText, href: '/app/templates' },
  { id: 'import-contacts', label: 'Import Contacts', icon: Users, href: '/app/contacts' },
  { id: 'send-message', label: 'Send Message', icon: MessageSquare, href: '/app/messages' },
  { id: 'keyword-bots', label: 'Keyword Bots', icon: Bot, href: '/app/keyword-bots' },
];

// ==================== CAMPAIGN STATUS CONFIG ====================

export interface StatusConfig {
  label: string;
  color: string;
  bgColor: string;
  icon: LucideIcon;
}

export const CAMPAIGN_STATUS_CONFIG: Record<string, StatusConfig> = {
  RUNNING: {
    label: 'Running',
    color: 'text-(--color-signal-orange)',
    bgColor: 'bg-(--color-signal-orange)/10',
    icon: Zap,
  },
  SCHEDULED: {
    label: 'Scheduled',
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    icon: Clock,
  },
  COMPLETED: {
    label: 'Completed',
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
    icon: CheckCircle2,
  },
  PAUSED: {
    label: 'Paused',
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/10',
    icon: Clock,
  },
  FAILED: {
    label: 'Failed',
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
    icon: XCircle,
  },
  DRAFT: {
    label: 'Draft',
    color: 'text-(--color-muted-gray)',
    bgColor: 'bg-(--color-muted-gray)/10',
    icon: FileText,
  },
  CANCELLED: {
    label: 'Cancelled',
    color: 'text-gray-400',
    bgColor: 'bg-gray-500/10',
    icon: XCircle,
  },
};

// ==================== TEMPLATE STATUS CONFIG ====================

export const TEMPLATE_STATUS_CONFIG: Record<string, StatusConfig> = {
  APPROVED: {
    label: 'Approved',
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
    icon: CheckCircle2,
  },
  PENDING: {
    label: 'Pending',
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/10',
    icon: Clock,
  },
  REJECTED: {
    label: 'Rejected',
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
    icon: XCircle,
  },
  DISABLED: {
    label: 'Disabled',
    color: 'text-gray-400',
    bgColor: 'bg-gray-500/10',
    icon: AlertCircle,
  },
};

// ==================== ACTIVITY TYPE CONFIG ====================

export interface ActivityTypeConfig {
  icon: LucideIcon;
  color: string;
  bgColor: string;
}

export const ACTIVITY_TYPE_CONFIG: Record<string, ActivityTypeConfig> = {
  message_sent: {
    icon: Send,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
  },
  message_received: {
    icon: MessageSquare,
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
  },
  campaign_started: {
    icon: Zap,
    color: 'text-(--color-signal-orange)',
    bgColor: 'bg-(--color-signal-orange)/10',
  },
  campaign_completed: {
    icon: CheckCircle2,
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
  },
  template_approved: {
    icon: CheckCircle2,
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
  },
  template_rejected: {
    icon: XCircle,
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
  },
  contact_added: {
    icon: Users,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
  },
  bot_triggered: {
    icon: Bot,
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/10',
  },
};

// ==================== CHART COLORS ====================

export const CHART_COLORS = {
  sent: '#f97316', // Orange
  delivered: '#22c55e', // Green
  read: '#3b82f6', // Blue
  failed: '#ef4444', // Red
} as const;

// ==================== CHANGE TYPE ICONS ====================

export const CHANGE_TYPE_CONFIG = {
  positive: {
    icon: TrendingUp,
    color: 'text-(--color-success)',
  },
  negative: {
    icon: TrendingDown,
    color: 'text-(--color-error)',
  },
  neutral: {
    icon: null,
    color: 'text-(--color-muted-gray)',
  },
} as const;

// ==================== REFRESH INTERVALS ====================

export const REFRESH_INTERVALS = {
  DASHBOARD: 60000, // 1 minute
  ACTIVITY: 30000, // 30 seconds
  STATS: 120000, // 2 minutes
} as const;
